import React from 'react';
import { AlertCircle, XCircle } from 'lucide-react';
import type { MissingReferenceInfo } from '@schemas/api';

interface MissingReferencesAlertProps {
  missingReferences?: MissingReferenceInfo[];
  hasBlockingIssues?: boolean;
}

export const MissingReferencesAlert: React.FC<MissingReferencesAlertProps> = ({
  missingReferences = [],
  hasBlockingIssues = false,
}) => {
  if (missingReferences.length === 0) {
    return null;
  }

  const unresolvedCritical = missingReferences.filter(
    (ref) => ref.status === 'unresolved' && ref.is_critical
  );

  const unresolvedNonCritical = missingReferences.filter(
    (ref) => ref.status === 'unresolved' && !ref.is_critical
  );

  const resolved = missingReferences.filter((ref) => ref.status !== 'unresolved');

  return (
    <div className="space-y-3">
      {/* Critical Missing References - Blocking */}
      {unresolvedCritical.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-start gap-3">
            <XCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-red-900">Missing Required References</h3>
              <p className="text-sm text-red-800 mt-1">
                These fields reference data that doesn't exist in the system. You must create them before approval.
              </p>
              <div className="mt-2 space-y-1">
                {unresolvedCritical.map((ref) => (
                  <div key={`${ref.field_name}-${ref.ref_value}`} className="text-sm text-red-800">
                    <span className="font-medium">{ref.field_name}</span>: {ref.ref_value} (
                    <span className="italic">{ref.ref_object_type}</span>)
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Non-Critical Missing References - Warning */}
      {unresolvedNonCritical.length > 0 && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-yellow-900">Missing Optional References</h3>
              <p className="text-sm text-yellow-800 mt-1">
                These optional fields reference data that doesn't exist. You can create them or skip.
              </p>
              <div className="mt-2 space-y-1">
                {unresolvedNonCritical.map((ref) => (
                  <div key={`${ref.field_name}-${ref.ref_value}`} className="text-sm text-yellow-800">
                    <span className="font-medium">{ref.field_name}</span>: {ref.ref_value} (
                    <span className="italic">{ref.ref_object_type}</span>)
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resolved References - Info */}
      {resolved.length > 0 && (
        <div className="rounded-lg border border-primary-200 bg-primary-50 p-4">
          <div className="flex items-start gap-3">
            <div className="h-5 w-5 text-primary-600 mt-0.5 flex-shrink-0">✓</div>
            <div className="flex-1">
              <h3 className="font-semibold text-primary-900">Resolved References</h3>
              <div className="mt-2 space-y-1">
                {resolved.map((ref) => (
                  <div key={`${ref.field_name}-${ref.ref_value}`} className="text-sm text-primary-800">
                    <span className="font-medium">{ref.field_name}</span>: {ref.ref_value} (
                    <span className="italic">{ref.status}</span>)
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
